import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Login/Login.css';
import './ForgotPassword.css';
import backgroundImage from '../../assets/images/Background.jpeg';
import logo from '../../assets/images/logo.png';
import { requestPasswordUpdate, resetPassword } from '../../api/services/authService';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1: Request OTP, 2: Reset Password
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    if (!emailOrPhone.trim()) {
      setMessage({ text: 'Please enter your email or phone number', type: 'error' });
      return;
    }

    setIsLoading(true);

    try {
      // Determine if input is email or phone
      const isEmail = emailOrPhone.includes('@');
      let phone = null;
      let email = null;

      if (isEmail) {
        email = emailOrPhone.trim();
      } else {
        // Format phone number
        phone = emailOrPhone.trim();
        if (!phone.startsWith('+')) {
          if (phone.startsWith('0')) {
            phone = '+254' + phone.substring(1);
          } else if (phone.startsWith('254')) {
            phone = '+' + phone;
          } else {
            phone = '+254' + phone;
          }
        }
        
        // Validate phone number format: +254 followed by exactly 9 digits
        const phoneRegex = /^\+254\d{9}$/;
        if (!phoneRegex.test(phone)) {
          setMessage({ 
            text: 'Phone number must be in format +254700000000 (+254 followed by 9 digits)', 
            type: 'error' 
          });
          setIsLoading(false);
          return;
        }
        
        setPhoneNumber(phone);
      }

      const response = await requestPasswordUpdate(email, phone);

      if (response.status === 'success') {
        setMessage({
          text: 'OTP has been sent to the registered phone.',
          type: 'success'
        });
        
        // If email was provided but phone wasn't set, we need phone for reset-password API
        // For now, if email was used, we'll need phone number - but user wants to remove phone field from step 2
        // So we'll require phone to be entered in step 1, or handle it differently
        // Move to step 2 after showing success message
        setTimeout(() => {
          setCurrentStep(2);
        }, 2000);
      } else {
        setMessage({ text: response.message || 'Failed to send OTP. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Request OTP error:', error);
      setMessage({ text: error.message || 'Failed to send OTP. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    if (!phoneNumber.trim()) {
      setMessage({ text: 'Please enter your phone number', type: 'error' });
      return;
    }

    if (!otpCode.trim() || otpCode.length !== 6) {
      setMessage({ text: 'Please enter a valid 6-digit OTP code', type: 'error' });
      return;
    }

    if (!newPassword.trim()) {
      setMessage({ text: 'Please enter a new password', type: 'error' });
      return;
    }

    // Password requirements (must match backend): 8+ chars, uppercase, lowercase, digit.
    const passwordRequirements = {
      minLength: newPassword.length >= 8,
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
      hasDigit: /\d/.test(newPassword),
    };

    if (!passwordRequirements.minLength || !passwordRequirements.hasUppercase || !passwordRequirements.hasLowercase || !passwordRequirements.hasDigit) {
      setMessage({
        text: 'Password must be at least 8 characters and include at least one uppercase letter, one lowercase letter, and one digit.',
        type: 'error',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ text: 'Passwords do not match', type: 'error' });
      return;
    }

    setIsLoading(true);

    try {
      // Ensure phone number has + prefix
      let phone = phoneNumber.trim();
      if (!phone.startsWith('+')) {
        if (phone.startsWith('0')) {
          phone = '+254' + phone.substring(1);
        } else if (phone.startsWith('254')) {
          phone = '+' + phone;
        } else {
          phone = '+254' + phone;
        }
      }

      const response = await resetPassword(phone, otpCode, newPassword);

      if (response.status === 'success') {
        setMessage({
          text: response.message || 'Password reset successfully. Log in with your new password.',
          type: 'success'
        });

        // Redirect to login after success
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setMessage({ text: response.message || 'Failed to reset password. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setMessage({ text: error.message || 'Failed to reset password. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-background-blur" style={{ backgroundImage: `url(${backgroundImage})` }}></div>
      <div className="forgot-password-overlay">
        <div className="forgot-password-wrapper">
          {/* Left Section - Welcome */}
          <div className="forgot-password-welcome-section">
            <div className="welcome-content">
              <img src={logo} alt="Tenant Score Logo" className="welcome-logo" />
              <div className="welcome-text">
                <span className="welcome-hello">Hello</span>
                <span className="welcome-main">welcome</span>
              </div>
              <div className="contact-separator"></div>
              <div className="contact-details-section">
                <h3 className="contact-title">Contact Details</h3>
                <p className="contact-address">P. O. Box 12345, Nairobi, Kenya</p>
                <div className="contact-info">
                  <span className="contact-label">Email:</span>
                  <a href="mailto:info@tenantscore.com" className="contact-value contact-link">info@tenantscore.com</a>
                </div>
                <div className="contact-info">
                  <span className="contact-label">Tel:</span>
                  <a href="tel:+254700000000" className="contact-value contact-link">+254 700 000 000</a>
                  <span className="contact-separator-inline">|</span>
                  <span className="contact-label">Cell:</span>
                  <a href="tel:+254711000000" className="contact-value contact-link">+254 711 000 000</a>
                </div>
                <div className="contact-info">
                  <span className="contact-label">WhatsApp:</span>
                  <a href="https://wa.me/254722000000" className="contact-value contact-link">+254 722 000 000</a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Form */}
          <div className="forgot-password-form-section">
            {currentStep === 1 ? (
              <form className="forgot-password-form login-form" onSubmit={handleRequestOTP}>
                <h2 className="form-title">Forgot Password</h2>
                <p className="form-subtitle">
                  Enter your email or phone number to receive an OTP code for password reset. If you enter your email, please use your phone number in the next step.
                </p>

                {message.text && (
                  <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                    {message.text}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="emailOrPhone">Email or Phone Number</label>
                  <input
                    type="text"
                    id="emailOrPhone"
                    className="form-input"
                    placeholder="Enter your email or phone number"
                    value={emailOrPhone}
                    onChange={(e) => setEmailOrPhone(e.target.value)}
                    required
                  />
                </div>

                <div className="form-buttons">
                  <button type="button" className="btn-signup" onClick={() => navigate('/login')}>
                    Back to Login
                  </button>
                  <button type="submit" className="btn-login" disabled={isLoading}>
                    {isLoading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </div>
              </form>
            ) : (
              <form className="forgot-password-form login-form" onSubmit={handleResetPassword}>
                <h2 className="form-title">Reset Password</h2>
                <p className="form-subtitle">
                  Enter your phone number, OTP code, new password, and confirm it below.
                </p>

                {message.text && (
                  <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                    {message.text}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="text"
                    id="phone"
                    className="form-input"
                    placeholder="Enter your phone number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="otp">OTP Code</label>
                  <input
                    type="text"
                    id="otp"
                    className="form-input"
                    placeholder="Enter 6-digit OTP"
                    value={otpCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtpCode(value);
                    }}
                    maxLength={6}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="newPassword"
                      className="form-input"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="1" y1="1" x2="23" y2="23" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {newPassword && (
                    <ul className="password-strength-list" aria-live="polite">
                      <li className={newPassword.length >= 8 ? 'req-met' : 'req-unmet'}>
                        {newPassword.length >= 8 ? '✓' : '•'} At least 8 characters
                      </li>
                      <li className={/[A-Z]/.test(newPassword) ? 'req-met' : 'req-unmet'}>
                        {/[A-Z]/.test(newPassword) ? '✓' : '•'} One uppercase letter
                      </li>
                      <li className={/[a-z]/.test(newPassword) ? 'req-met' : 'req-unmet'}>
                        {/[a-z]/.test(newPassword) ? '✓' : '•'} One lowercase letter
                      </li>
                      <li className={/\d/.test(newPassword) ? 'req-met' : 'req-unmet'}>
                        {/\d/.test(newPassword) ? '✓' : '•'} One digit
                      </li>
                    </ul>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      className="form-input"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <line x1="1" y1="1" x2="23" y2="23" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="3" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="form-buttons">
                  <button type="button" className="btn-signup" onClick={() => navigate('/login')}>
                    Back to Login
                  </button>
                  <button type="submit" className="btn-login" disabled={isLoading}>
                    {isLoading ? 'Resetting Password...' : 'Reset Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
